import { Chart, registerables } from 'chart.js';
import 'chartjs-adapter-date-fns';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { DataService } from '../../services/dataService';
import { supabaseClient } from '../../services/supabase';
import { supabaseLocationService } from '../../services/supabaseLocationService';
import { downloadChartAsPNG, downloadDataAsCSV, formatFilename } from '../../utils/downloadUtils';
import { standardTitle } from '../../utils/chartFormatting';
import './ChartLayout.css';
import { DataTable } from './DataTable';

Chart.register(...registerables);

export interface VMTMetrics {
  currentVMT: number;
  changeSince2005: number;
  californiaAverage: number;
  totalVMT2023: number;
}

// VMTPerCapitaMetrics interface removed - no longer needed

interface VMTChartProps {
  location: string;
  geofips: number | null;
  chartView?: 'total' | 'percapita';
  emfac2005_2014Method?: 'EMFAC2025' | 'Population' | 'Combined';
  backcastingMethod?: 'AADT' | 'EMFAC2025' | 'EMFAC Fleet' | 'EMFAC2021 Fleet' | 'Population' | 'Combined';
  emfacFleetVersion?: '2021' | '2025';
  embedOnly?: boolean;
  onMetricsCalculated?: (metrics: VMTMetrics) => void;
  onDataCalculated?: (data: any[]) => void;
  // Expose data table functionality for parent ChartLayout
  onDataTableReady?: (content: React.ReactNode, toggleFn: () => void) => void;
  // Expose download functionality for parent ChartLayout
  onDownloadPNGReady?: (handler: () => void) => void;
  onDownloadCSVReady?: (handler: () => void) => void;
}

export const VMTChart: React.FC<VMTChartProps> = ({ location, geofips, chartView = 'total', emfac2005_2014Method = 'EMFAC2025', backcastingMethod = 'AADT', emfacFleetVersion = '2025', embedOnly = false, onMetricsCalculated, onDataCalculated, onDataTableReady, onDownloadPNGReady, onDownloadCSVReady }) => {
  const chartRef = useRef<HTMLCanvasElement>(null);
  const chartInstance = useRef<Chart | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [chartData, setChartData] = useState<any[]>([]);
  const [tableData, setTableData] = useState<any[]>([]);
  const [originalData, setOriginalData] = useState<any[]>([]);
  const [showDataTable, setShowDataTable] = useState(false);
  const [modifiedYears, setModifiedYears] = useState<Set<number>>(new Set());
  const chartCreationInProgress = useRef(false);


  // Function to calculate California average per capita VMT using EMFAC2025_county_filtered
  const calculateCaliforniaPerCapitaVMT = async (years: number[]) => {
    try {
      console.log('calculateCaliforniaPerCapitaVMT called with years:', years);
      
      // Fetch California population data
      const { data: californiaPopData, error: popError } = await supabaseClient
        .from('population')
        .select('year, population')
        .eq('geofips', 6) // California state FIPS code (no leading zero)
        .eq('geography_type', 'State')
        .in('year', years)
        .order('year', { ascending: true });

      if (popError) {
        console.error('Error fetching California population data:', popError);
        return years.map(() => 0);
      }

      console.log('California population data:', californiaPopData);

      // Fetch all county-level VMT data from EMFAC2025_county_filtered
      console.log('Fetching California VMT from emfac2025_county_filtered (sum of all counties)...');
      const { data: countyVMTData, error: vmtError } = await supabaseClient
        .from('emfac2025_county_filtered')
        .select('year, total_vmt')
        .in('year', years)
        .order('year', { ascending: true });

      if (vmtError) {
        console.error('Error fetching California county VMT data:', vmtError);
        return years.map(() => 0);
      }

      console.log(`Fetched ${countyVMTData?.length || 0} county VMT records`);

      // Sum VMT by year across all counties to get California total VMT
      const californiaVMTByYear = new Map<number, number>();
      (countyVMTData || []).forEach((record: any) => {
        const year = record.year;
        const vmt = parseFloat(record.total_vmt) || 0;
        if (californiaVMTByYear.has(year)) {
          californiaVMTByYear.set(year, californiaVMTByYear.get(year)! + vmt);
        } else {
          californiaVMTByYear.set(year, vmt);
        }
      });

      console.log('California total VMT by year (sum of all counties):', Array.from(californiaVMTByYear.entries()).sort((a, b) => a[0] - b[0]));

      // Calculate per capita VMT for each year
      const perCapitaData = years.map(year => {
        const yearPopData = californiaPopData?.find(d => d.year === year);
        if (!yearPopData) {
          console.log(`No population data for California year ${year}`);
          return null;
        }

        const totalVMT = californiaVMTByYear.get(year) || 0;
        if (totalVMT === 0) {
          console.log(`No VMT data for California year ${year}`);
          return null;
        }

        const population = yearPopData.population || 1;
        const perCapita = (totalVMT / population) / 365; // Daily per capita VMT
        console.log(`California ${year}: VMT=${totalVMT.toLocaleString()}, Population=${population.toLocaleString()}, Daily Per Capita=${perCapita.toFixed(2)}`);
        return perCapita;
      });

      console.log('California per capita VMT data:', perCapitaData);
      return perCapitaData;
    } catch (error) {
      console.error('Error calculating California per capita VMT:', error);
      return years.map(() => 0);
    }
  };

  // Effect to ensure chart is created after component mounts
  useEffect(() => {
    // This effect runs after the component mounts and the canvas is available
    return () => {
      if (chartInstance.current) {
        chartInstance.current.destroy();
      }
    };
  }, []);

  useEffect(() => {
    const fetchVMTData = async () => {
      console.log('🚀 VMT Chart: Starting data fetch for', location, 'geofips:', geofips, 'chartView:', chartView);
      
      if (!location) {
        console.log('❌ VMT Chart: No location provided');
        return;
      }
      
      if (!geofips) {
        console.log('❌ VMT Chart: No geofips provided');
        setLoading(true);
        setError('Loading location data...');
        return;
      }
      
      try {
        setLoading(true);
        setError(null);
        
        // Fetch raw Replica data, AADT backcasted data, and EMFAC backcasting data
        
        // Fetch data with error handling for EMFAC
        let emfacData: any[] = [];
        let rawVmtData: any[] = [];
        let backcastedVmtData: any[] = [];
        
        try {
          
          // Determine emfacVersion and emfacFleetVersion based on backcasting method
          // EMFAC2021 Fleet: use 2021 for both (no fallback to 2025)
          // EMFAC Fleet or EMFAC2025: use 2025 for both (no fallback to 2021)
          // Combined: use 2025 for county, but both fleet versions are used
          let emfacVersion: '2021' | '2025' = '2025';
          let fleetVersion: '2021' | '2025' = emfacFleetVersion;
          
          if (backcastingMethod === 'EMFAC2021 Fleet') {
            emfacVersion = '2021';
            fleetVersion = '2021';
          } else if (backcastingMethod === 'EMFAC Fleet' || backcastingMethod === 'EMFAC2025') {
            emfacVersion = '2025';
            fleetVersion = '2025';
          } else if (backcastingMethod === 'Combined') {
            // Combined uses 2025 for county, but both fleet versions
            emfacVersion = '2025';
            // fleetVersion stays as emfacFleetVersion (could be either)
          }
          
          const [rawVmtDataResult, backcastedVmtDataResult, emfacDataResult] = await Promise.all([
            DataService.getVMTData(location, undefined, geofips), // Raw Replica data using geofips
            DataService.getVMTDataWithScaling(geofips, backcastingMethod, emfacVersion, fleetVersion), // Backcasted data using selected method with correct version (no fallbacks)
            DataService.getEMFACBackcastingDataByGeofips(geofips, [2005, 2006, 2007, 2008, 2009, 2010, 2011, 2012, 2013, 2014], emfac2005_2014Method).catch(err => {
              console.warn('EMFAC data not available, continuing without it:', err);
              return [];
            }) // EMFAC backcasting data using geofips with selected version
          ]);
          
          rawVmtData = rawVmtDataResult;
          backcastedVmtData = backcastedVmtDataResult;
          emfacData = emfacDataResult;
        } catch (error) {
          console.error('Error fetching VMT data:', error);
          setError('Failed to load VMT data');
          setLoading(false);
          return;
        }
        
        // Get population data for per capita calculations
        let population = 120000; // Default estimate
        try {
          const populationData = await supabaseLocationService.getLatestPopulation(location);
          if (populationData && populationData.population) {
            population = populationData.population;
          }
        } catch (err) {
          console.warn('Population data not available, using default:', err);
        }
        
        // Filter to only include PRIVATE_AUTO and ON_DEMAND modes for both datasets
        const rawPrivateAuto = rawVmtData.filter(d => d.mode === 'PRIVATE_AUTO');
        const rawOnDemand = rawVmtData.filter(d => d.mode === 'ON_DEMAND_AUTO');
        const backcastedPrivateAuto = backcastedVmtData.filter(d => d.mode === 'PRIVATE_AUTO');
        const backcastedOnDemand = backcastedVmtData.filter(d => d.mode === 'ON_DEMAND_AUTO');
        
        console.log('📊 VMT Chart Data Summary:');
        console.log('  Raw VMT data years:', Array.from(new Set(rawVmtData.map(d => d.year))).sort());
        console.log('  Backcasted VMT data years:', Array.from(new Set(backcastedVmtData.map(d => d.year))).sort());
        console.log('  Backcasted Private Auto years:', Array.from(new Set(backcastedPrivateAuto.map(d => d.year))).sort());
        console.log('  Backcasted On Demand years:', Array.from(new Set(backcastedOnDemand.map(d => d.year))).sort());
        
        // Process EMFAC data for backcasting (2005-2014 only)
        
        // Get 2015 VMT data as the baseline for EMFAC backcasting
        const vmt2015PrivateAuto = backcastedPrivateAuto.find(d => d.year === 2015);
        const vmt2015OnDemand = backcastedOnDemand.find(d => d.year === 2015);
        const vmt2015Total = (vmt2015PrivateAuto?.vmt || 0) + (vmt2015OnDemand?.vmt || 0);
        
        
        // Create EMFAC backcasted data for years 2005-2014 only
        // Backcasting ratios are: current_year_vmt / next_year_vmt
        // So we need to apply them sequentially, starting from 2015 and working backwards
        const emfacBackcastedData: any[] = [];
        const emfacYears = [2005, 2006, 2007, 2008, 2009, 2010, 2011, 2012, 2013, 2014];
        
        if (vmt2015Total > 0 && emfacData.length > 0) {
          console.log('EMFAC data for backcasting:', emfacData);
          console.log('VMT 2015 total:', vmt2015Total);
          
          // Sort ratios by year descending (2014, 2013, ..., 2005)
          // This ensures we apply ratios sequentially from 2015 backwards
          const sortedRatios = emfacData
            .filter(r => emfacYears.includes(r.year))
            .sort((a, b) => b.year - a.year);
          
          // Calculate VMT for each year sequentially
          // Start with 2015 as baseline, then apply each ratio to get the previous year
          const vmtByYear = new Map<number, number>();
          vmtByYear.set(2015, vmt2015Total);
          
          // Apply ratios sequentially: year Y = year Y+1 * ratio_Y
          // where ratio_Y = VMT_Y / VMT_Y+1
          for (const emfacRecord of sortedRatios) {
            const nextYear = emfacRecord.year + 1;
            const nextYearVMT = vmtByYear.get(nextYear);
            
            if (nextYearVMT && emfacRecord.ratio) {
              // Apply ratio: current_year = next_year * ratio
              // Since ratio = current_vmt / next_vmt, we get: current = next * ratio
              const currentYearVMT = nextYearVMT * emfacRecord.ratio;
              vmtByYear.set(emfacRecord.year, currentYearVMT);
              
              console.log(`EMFAC backcasting ${emfacRecord.year}: nextYearVMT=${nextYearVMT.toLocaleString()}, ratio=${emfacRecord.ratio.toFixed(4)}, currentYearVMT=${currentYearVMT.toLocaleString()}`);
            }
          }
          
          // Now create the data points for each year, distributing between PRIVATE_AUTO and ON_DEMAND
          // Use the same proportion as 2015 for all years
          const privateAutoRatio = vmt2015PrivateAuto ? vmt2015PrivateAuto.vmt / vmt2015Total : 0.8; // Default to 80% private auto
          const onDemandRatio = 1 - privateAutoRatio;
          
          for (const year of emfacYears) {
            const yearVMT = vmtByYear.get(year);
            if (yearVMT !== undefined && yearVMT > 0) {
              emfacBackcastedData.push({
                location_name: location,
                year: year,
                mode: 'PRIVATE_AUTO',
                vmt: yearVMT * privateAutoRatio
              });
              
              emfacBackcastedData.push({
                location_name: location,
                year: year,
                mode: 'ON_DEMAND_AUTO',
                vmt: yearVMT * onDemandRatio
              });
            }
          }
        } else {
          console.log('EMFAC backcasting skipped - vmt2015Total:', vmt2015Total, 'emfacData.length:', emfacData.length);
        }
        
        
        if (rawPrivateAuto.length === 0 && rawOnDemand.length === 0) {
          setError('No VMT data available for this location');
          setLoading(false);
          return;
        }

        // Get all available years (combine EMFAC backcasted years 2005-2014 with AADT backcasted years 2015-2023)
        const emfacPrivateAuto = emfacBackcastedData.filter(d => d.mode === 'PRIVATE_AUTO');
        const emfacOnDemand = emfacBackcastedData.filter(d => d.mode === 'ON_DEMAND_AUTO');
        
        console.log('  EMFAC backcasted years:', Array.from(new Set(emfacBackcastedData.map(d => d.year))).sort());
        
        const allYears = Array.from(new Set([
          ...emfacPrivateAuto.map(d => d.year),
          ...emfacOnDemand.map(d => d.year),
          ...backcastedPrivateAuto.map(d => d.year),
          ...backcastedOnDemand.map(d => d.year),
          ...rawPrivateAuto.map(d => d.year),
          ...rawOnDemand.map(d => d.year)
        ])).sort();
        
        console.log('  All years for chart:', allYears);
        console.log('  Combined data length:', allYears.length);

        // Create a single dataset with different colors for different data sources
        const combinedData = allYears.map(year => {
          if (year === 2023 || year === 2024 || year === 2025) {
            // Use Replica data for 2023, 2024, and 2025
            const privateAutoVmt = rawPrivateAuto.find(d => d.year === year)?.vmt || 0;
            const onDemandVmt = rawOnDemand.find(d => d.year === year)?.vmt || 0;
            return privateAutoVmt + onDemandVmt;
          } else if (year >= 2005 && year <= 2014) {
            // Use EMFAC backcasted data for 2005-2014
            const privateAutoVmt = emfacPrivateAuto.find(d => d.year === year)?.vmt || 0;
            const onDemandVmt = emfacOnDemand.find(d => d.year === year)?.vmt || 0;
            const totalVmt = privateAutoVmt + onDemandVmt;
            return totalVmt;
          } else {
            // Use AADT backcasted data for 2015-2022
            const privateAutoVmt = backcastedPrivateAuto.find(d => d.year === year)?.vmt || 0;
            const onDemandVmt = backcastedOnDemand.find(d => d.year === year)?.vmt || 0;
            return privateAutoVmt + onDemandVmt;
          }
        });
        
        // Create background colors array - different colors for different data sources
        const backgroundColors = allYears.map(year => {
          if (year === 2023 || year === 2024 || year === 2025) return '#3b82f6'; // Blue for Replica data
          if (year >= 2005 && year <= 2014) return '#f59e0b'; // Orange for EMFAC backcasted data
          return '#10b981'; // Green for AADT backcasted data
        });
        
        const borderColors = allYears.map(year => {
          if (year === 2023 || year === 2024 || year === 2025) return '#1e40af'; // Dark blue for Replica data
          if (year >= 2005 && year <= 2014) return '#d97706'; // Dark orange for EMFAC backcasted data
          return '#047857'; // Dark green for AADT backcasted data
        });
        

        // Calculate per capita data for the right axis
        const perCapitaData = combinedData.map(vmt => vmt / population / 365);
        
        // Create datasets based on chart view
        let datasets: any[] = [];
        
        if (chartView === 'total') {
          // Total VMT view - only show VMT bars
          datasets = [
            {
              label: 'VMT Data',
              data: combinedData,
              backgroundColor: backgroundColors,
              borderColor: borderColors,
              borderWidth: 1,
              yAxisID: 'y'
            }
          ];
        } else {
          // Per capita view - show line chart comparing location to California average
          // Calculate California average per capita VMT
          const californiaPerCapitaData = await calculateCaliforniaPerCapitaVMT(allYears);
          
          datasets = [
            {
              label: location,
              data: perCapitaData,
              type: 'line',
              borderColor: '#3b82f6',
              backgroundColor: 'rgba(59, 130, 246, 0.1)',
              borderWidth: 3,
              fill: false,
              pointRadius: 6,
              pointHoverRadius: 8,
              tension: 0.4
            },
            {
              label: 'California Average',
              data: californiaPerCapitaData,
              type: 'line',
              borderColor: '#ef4444',
              backgroundColor: 'rgba(239, 68, 68, 0.1)',
              borderWidth: 3,
              fill: false,
              pointRadius: (context: any) => {
                // Don't show points for null values (years 2005-2018)
                return (context.parsed.y === null || context.parsed.y === undefined) ? 0 : 6;
              },
              pointHoverRadius: 8,
              tension: 0.4,
              spanGaps: false // Don't draw lines through null values
            }
          ];
        }

        // Store chart data for download
        console.log('📊 VMT Chart: Creating download data with allYears:', allYears);
        console.log('📊 VMT Chart: Combined data length:', combinedData.length);
        
        const downloadData = allYears.map((year, index) => {
          let dataSource: string;
          if (year === 2023 || year === 2024 || year === 2025) {
            dataSource = 'Replica';
          } else if (year >= 2005 && year <= 2014) {
            if (emfac2005_2014Method === 'Combined') {
              dataSource = 'Combined Backcasted';
            } else if (emfac2005_2014Method === 'Population') {
              dataSource = 'Population Backcasted';
            } else {
              dataSource = 'EMFAC2025 Backcasted';
            }
          } else if (backcastingMethod === 'Combined') {
            dataSource = 'Combined Backcasted';
          } else if (backcastingMethod === 'EMFAC Fleet') {
            dataSource = 'EMFAC Fleet Backcasted';
          } else if (backcastingMethod === 'EMFAC2025') {
            dataSource = 'EMFAC2025 Backcasted';
          } else if (backcastingMethod === 'Population') {
            dataSource = 'Population Backcasted';
          } else {
            dataSource = 'AADT Backcasted';
          }
          const vmtValue = Math.round(combinedData[index]);
          
          // Warn if VMT is 0 for years 2005-2014 (EMFAC backcasting may have failed)
          if (vmtValue === 0 && year >= 2005 && year <= 2014) {
            console.warn(`⚠️ VMT Chart: VMT is 0 for year ${year} - EMFAC backcasting may have failed.`);
            console.warn(`   Check: vmt2015Total=${vmt2015Total}, emfacData.length=${emfacData.length}, emfacBackcastedData.length=${emfacBackcastedData.length}`);
          }
          
          return {
            year: year,
            vmt: vmtValue,
            data_source: dataSource,
            location: location
          };
        });
        
        console.log('📊 VMT Chart: Download data created:', downloadData);
        setChartData(downloadData);
        setTableData(downloadData);
        setOriginalData([...downloadData]); // Store a copy of original data
        
        // Notify parent component of data change
        if (onDataCalculated) {
          console.log('📊 VMT Chart calling onDataCalculated with data:', downloadData);
          console.log('📊 VMT Chart data length:', downloadData.length);
          console.log('📊 VMT Chart data years:', downloadData.map(d => d.year));
          onDataCalculated(downloadData);
        } else {
          console.log('⚠️ VMT Chart: onDataCalculated callback not provided');
        }

        
        if (datasets.length === 0) {
          setError('No VMT data available for the requested modes');
          setLoading(false);
          return;
        }

        // Create chart with proper timing
        const createChart = () => {
          if (chartCreationInProgress.current) {
            return;
          }
          
          if (!chartRef.current) {
            setTimeout(createChart, 100);
            return;
          }
          
          const ctx = chartRef.current.getContext('2d');
          if (!ctx) {
            setTimeout(createChart, 100);
            return;
          }
          
          chartCreationInProgress.current = true;

          // Destroy existing chart
          if (chartInstance.current) {
            chartInstance.current.destroy();
          }


          try {
            chartInstance.current = new Chart(ctx, {
              type: chartView === 'total' ? 'bar' : 'line',
              data: {
                labels: allYears.map(year => year.toString()),
                datasets: datasets
              },
              options: {
                responsive: true,
                maintainAspectRatio: false,
                animation: false,
                layout: {
                  padding: {
                    left: 10,
                    right: 10,
                    top: 10,
                    bottom: 10
                  }
                },
                plugins: {
                  title: embedOnly
                    ? { display: false }
                    : standardTitle(
                        `Vehicle Miles Traveled (${chartView === 'total' ? 'Total' : 'Per Capita'}) - ${location}`
                      ),
                  legend: {
                    display: !embedOnly,
                    position: 'bottom',
                    labels: {
                      usePointStyle: true,
                      padding: 10,
                      generateLabels: function(chart: any) {
                        const labels: any[] = [];
                        
                        if (chartView === 'total') {
                          // Total view - show data source legend
                          const hasReplicaData = backgroundColors.some(color => color === '#3b82f6');
                          const hasEmfacData = backgroundColors.some(color => color === '#f59e0b');
                          const hasAadtData = backgroundColors.some(color => color === '#10b981');
                          const hasCombinedData = downloadData.some((d: any) => d.data_source === 'Combined Backcasted');
                          const hasEmfacFleetData = downloadData.some((d: any) => d.data_source === 'EMFAC Fleet Backcasted');
                          
                          // Determine which Replica years are present
                          const replicaYears = downloadData
                            .filter((d: any) => d.data_source === 'Replica')
                            .map((d: any) => d.year)
                            .sort((a: number, b: number) => a - b);
                          
                          if (hasEmfacData) {
                            let labelText: string;
                            if (downloadData.some((d: any) => d.data_source === 'Combined Backcasted')) {
                              labelText = 'Combined Backcasted (2005-2014)';
                            } else if (downloadData.some((d: any) => d.data_source === 'Population Backcasted')) {
                              labelText = 'Population Backcasted (2005-2014)';
                            } else {
                              labelText = 'EMFAC Backcasted (2005-2014)';
                            }
                            labels.push({
                              text: labelText,
                              fillStyle: '#f59e0b',
                              strokeStyle: '#f59e0b',
                              lineWidth: 1,
                              pointStyle: 'rect' as const,
                              hidden: false,
                              index: 0
                            });
                          }
                          if (hasCombinedData) {
                            labels.push({
                              text: 'Combined Backcasted (2015-2022)',
                              fillStyle: '#10b981',
                              strokeStyle: '#10b981',
                              lineWidth: 1,
                              pointStyle: 'rect' as const,
                              hidden: false,
                              index: 0
                            });
                          } else if (hasEmfacFleetData) {
                            labels.push({
                              text: 'EMFAC Fleet Backcasted (2015-2022)',
                              fillStyle: '#10b981',
                              strokeStyle: '#10b981',
                              lineWidth: 1,
                              pointStyle: 'rect' as const,
                              hidden: false,
                              index: 0
                            });
                          } else if (hasAadtData) {
                            labels.push({
                              text: 'AADT Backcasted (2015-2022)',
                              fillStyle: '#10b981',
                              strokeStyle: '#10b981',
                              lineWidth: 1,
                              pointStyle: 'rect' as const,
                              hidden: false,
                              index: 1
                            });
                          }
                          if (hasReplicaData) {
                            // Create label with year range
                            const replicaYearRange = replicaYears.length > 0 
                              ? `(${replicaYears.join(', ')})`
                              : '(2023-2025)';
                            labels.push({
                              text: `Replica Data ${replicaYearRange}`,
                              fillStyle: '#3b82f6',
                              strokeStyle: '#3b82f6',
                              lineWidth: 1,
                              pointStyle: 'rect' as const,
                              hidden: false,
                              index: 2
                            });
                          }
                        } else {
                          // Per capita view - show location and California average
                          labels.push({
                            text: location,
                            fillStyle: '#3b82f6',
                            strokeStyle: '#3b82f6',
                            lineWidth: 3,
                            pointStyle: 'circle' as const,
                            hidden: false,
                            index: 0
                          });
                          labels.push({
                            text: 'California Average',
                            fillStyle: '#ef4444',
                            strokeStyle: '#ef4444',
                            lineWidth: 3,
                            pointStyle: 'circle' as const,
                            hidden: false,
                            index: 1
                          });
                        }
                        return labels;
                      }
                    }
                  }
                },
                scales: {
                  x: {
                    title: {
                      display: true,
                      text: 'Year',
                      font: {
                        weight: ('bold' as const)
                      }
                    },
                    grid: {
                      display: false
                    }
                  },
                  y: {
                    type: 'linear',
                    display: true,
                    position: 'left',
                    title: {
                      display: true,
                      text: chartView === 'total' ? 'Vehicle VMT (miles)' : 'Daily Vehicle VMT per Capita (miles)',
                      font: {
                        weight: ('bold' as const)
                      }
                    },
                    beginAtZero: true,
                    grid: {
                      color: 'rgba(0, 0, 0, 0.1)'
                    },
                    ticks: chartView === 'percapita' ? {
                      callback: function(value: any) {
                        return value.toFixed(1);
                      }
                    } : {
                      callback: function(value: any) {
                        // Format numbers with commas for thousands
                        if (typeof value === 'number') {
                          return value.toLocaleString('en-US', { maximumFractionDigits: 0 });
                        }
                        return value;
                      }
                    }
                  }
                },
                interaction: {
                  intersect: false,
                  mode: 'index'
                }
              }
            });

            chartCreationInProgress.current = false;
          } catch (chartError) {
            console.error('Error creating chart:', chartError);
            setError('Failed to create chart');
            setLoading(false);
            chartCreationInProgress.current = false;
            return;
          }
        };

        // Start chart creation
        createChart();


        // Calculate metrics for the parent component
        if (onMetricsCalculated && (backcastedVmtData.length > 0 || emfacBackcastedData.length > 0)) {
          
          // Get population data from Supabase location service
          let population = 120000; // Default estimate
          try {
            const populationData = await supabaseLocationService.getLatestPopulation(location);
            if (populationData && populationData.population) {
              population = populationData.population;
            } else {
            }
          } catch (err) {
          }
          
          // Get the most recent year's data for current VMT
          const latestYear = Math.max(...allYears);
          let latestData: any[] = [];
          
          if (latestYear === 2023 || latestYear === 2024 || latestYear === 2025) {
            // Use raw Replica data for 2023, 2024, and 2025
            latestData = rawVmtData.filter((d: any) => d.year === latestYear);
          } else if (latestYear >= 2005 && latestYear <= 2014) {
            // Use EMFAC backcasted data for 2005-2014
            latestData = emfacBackcastedData.filter((d: any) => d.year === latestYear);
          } else {
            // Use AADT backcasted data for 2015-2022
            latestData = backcastedVmtData.filter((d: any) => d.year === latestYear);
          }
          
          
          // Calculate VMT as PRIVATE_AUTO + ON_DEMAND_AUTO (personal vehicle usage)
          const privateAutoLatest = latestData.filter((d: any) => d.mode === 'PRIVATE_AUTO');
          const onDemandLatest = latestData.filter((d: any) => d.mode === 'ON_DEMAND_AUTO');
          const privateAutoComparison = backcastedVmtData.filter((d: any) => d.mode === 'PRIVATE_AUTO');
          const onDemandComparison = backcastedVmtData.filter((d: any) => d.mode === 'ON_DEMAND_AUTO');
          
          if (privateAutoLatest.length > 0) {
            // Calculate combined VMT for latest year (PRIVATE_AUTO + ON_DEMAND_AUTO)
            const privateAutoVMT = privateAutoLatest[0].vmt;
            const onDemandVMT = onDemandLatest.length > 0 ? onDemandLatest[0].vmt : 0;
            const currentVMT = privateAutoVMT + onDemandVMT;
            
            // Get 2005 data for comparison (earliest year with EMFAC backcasting)
            const earliestYear = Math.min(...allYears);
            const comparisonYear = allYears.includes(2005) ? 2005 : earliestYear;
            
            let privateAutoComparisonData: any[] = [];
            let onDemandComparisonData: any[] = [];
            
            if (comparisonYear >= 2005 && comparisonYear <= 2014) {
              // Use EMFAC backcasted data for comparison
              const emfacComparisonPrivateAuto = emfacBackcastedData.filter((d: any) => d.mode === 'PRIVATE_AUTO' && d.year === comparisonYear);
              const emfacComparisonOnDemand = emfacBackcastedData.filter((d: any) => d.mode === 'ON_DEMAND_AUTO' && d.year === comparisonYear);
              privateAutoComparisonData = emfacComparisonPrivateAuto;
              onDemandComparisonData = emfacComparisonOnDemand;
            } else {
              // Use AADT backcasted data for comparison
              privateAutoComparisonData = privateAutoComparison.filter((d: any) => d.year === comparisonYear);
              onDemandComparisonData = onDemandComparison.filter((d: any) => d.year === comparisonYear);
            }
            
            const privateAutoComparisonVMT = privateAutoComparisonData.length > 0 ? privateAutoComparisonData[0].vmt : 0;
            const onDemandComparisonVMT = onDemandComparisonData.length > 0 ? onDemandComparisonData[0].vmt : 0;
            const vmtComparison = privateAutoComparisonVMT + onDemandComparisonVMT;
            
            
            // The VMT values appear to be annual total VMT, not per capita
            // We need to convert to daily per capita by:
            // 1. Dividing by population to get per capita
            // 2. Dividing by 365 to get daily values
            
            
            const estimatedPopulation = population;
            
            const currentVMTPerCapitaAnnual = currentVMT / estimatedPopulation;
            const vmtComparisonPerCapitaAnnual = vmtComparison / estimatedPopulation;
            
            // Convert annual VMT to daily per capita
            const currentVMTPerCapitaDaily = currentVMTPerCapitaAnnual / 365;
            const vmtComparisonPerCapitaDaily = vmtComparisonPerCapitaAnnual / 365;
            
            
            // Try to get actual California VMT data
            let californiaAverage = 15.8; // Default hardcoded value
            
            try {
              // Query for California state-level VMT data (avoid "California City")
              const { data: californiaVMTData, error: caError } = await supabaseClient
                .from('replica_vmt_by_mode')
                .select('year, mode, vmt')
                .eq('geofips', 6)
                .order('year', { ascending: false })
                .limit(20);
              
              if (!caError && californiaVMTData && californiaVMTData.length > 0) {
                
                // Get latest year California data
                const latestYear = Math.max(...californiaVMTData.map(d => d.year));
                const californiaLatest = californiaVMTData.filter(d => d.year === latestYear);
                
                // Calculate California VMT (PRIVATE_AUTO + ON_DEMAND_AUTO)
                const californiaPrivateAuto = californiaLatest.filter(d => d.mode === 'PRIVATE_AUTO');
                const californiaOnDemand = californiaLatest.filter(d => d.mode === 'ON_DEMAND_AUTO');
                
                const californiaPrivateAutoVMT = californiaPrivateAuto.length > 0 ? californiaPrivateAuto[0].vmt : 0;
                const californiaOnDemandVMT = californiaOnDemand.length > 0 ? californiaOnDemand[0].vmt : 0;
                const californiaTotalVMT = californiaPrivateAutoVMT + californiaOnDemandVMT;
                
                
                // Get California population from Supabase location service
                let californiaPopulation = 39000000; // Default estimate
                try {
                  const californiaData = await supabaseLocationService.getLatestPopulation('California');
                  if (californiaData && californiaData.population) {
                    californiaPopulation = californiaData.population;
                  } else {
                  }
                } catch (err) {
                }
                
                
                // Calculate California VMT per capita daily
                const californiaVMTPerCapitaDaily = (californiaTotalVMT / californiaPopulation) / 365;
                californiaAverage = californiaVMTPerCapitaDaily;
                
              } else {
              }
            } catch (err) {
            }
            
            
            // Calculate percentage change since comparison year
            const changeSinceComparison = vmtComparisonPerCapitaDaily > 0 ? ((currentVMTPerCapitaDaily - vmtComparisonPerCapitaDaily) / vmtComparisonPerCapitaDaily) * 100 : 0;
            
            // Get total VMT for latest Replica year from combinedData
            const latestReplicaYear = Math.max(...allYears.filter(y => y === 2023 || y === 2024 || y === 2025));
            const vmtLatestIndex = allYears.indexOf(latestReplicaYear);
            const totalVMTLatest = vmtLatestIndex >= 0 ? combinedData[vmtLatestIndex] : 0;
            
            const metrics: VMTMetrics = {
              currentVMT: Math.round(currentVMTPerCapitaDaily * 10) / 10, // Round to 1 decimal place
              changeSince2005: Math.round(changeSinceComparison * 10) / 10, // Round to 1 decimal
              californiaAverage: Math.round(californiaAverage * 10) / 10, // Round to 1 decimal place
              totalVMT2023: Math.round(totalVMTLatest) // Total VMT for latest Replica year in miles (kept as totalVMT2023 for backward compatibility)
            };
            
            onMetricsCalculated(metrics);
            
            // Per capita metrics calculation removed - no longer needed
            
            // Per capita data calculation removed - no longer needed
          } else {
          }
        } else {
        }

        setLoading(false);
      } catch (err) {
        console.error('Error fetching VMT data:', err);
        setError('Failed to load VMT data');
        setLoading(false);
      }
    };

    fetchVMTData();
  }, [location, geofips, chartView, emfac2005_2014Method, backcastingMethod, emfacFleetVersion, embedOnly, onMetricsCalculated, onDataCalculated]);

  const toggleDataTable = useCallback(() => {
    setShowDataTable(!showDataTable);
  }, [showDataTable]);

  // Define handlers before early returns so they can be used in hooks
  const scaleDataToModifiedValue = useCallback((data: any[], modifiedYear: number, modifiedVMT: number) => {
    const originalModifiedData = originalData.find(d => d.year === modifiedYear);
    if (!originalModifiedData) return data;

    const scalingFactor = modifiedVMT / originalModifiedData.vmt;
    
    return data.map(item => {
      // Don't scale if this year has been manually modified
      if (modifiedYears.has(item.year)) {
        return item;
      }
      
      const originalItem = originalData.find(d => d.year === item.year);
      if (!originalItem) return item;
      
      return {
        ...item,
        vmt: Math.round(originalItem.vmt * scalingFactor)
      };
    });
  }, [originalData, modifiedYears]);

  // Define updateChart before handleDataTableChange to avoid temporal dead zone error
  const updateChart = useCallback((data: any[]) => {
    if (chartInstance.current && data.length > 0) {
      const sortedData = data.sort((a, b) => a.year - b.year);
      const years = sortedData.map(d => d.year);
      const vmtValues = sortedData.map(d => Math.round(d.vmt));
      
      // Check if data has been modified by comparing with original data
      const getDataSourceColor = (dataPoint: any) => {
        const originalPoint = originalData.find(orig => orig.year === dataPoint.year);
        
        // If no original data point exists, it's a new/custom entry
        if (!originalPoint) {
          return '#dc2626'; // Red for new data
        }
        
        // If this year was manually modified, use red color
        if (modifiedYears.has(dataPoint.year)) {
          return '#dc2626'; // Red for modified data
        }
        
        // If data source has been changed from original, it's custom
        if (originalPoint.data_source !== dataPoint.data_source) {
          return '#9333ea'; // Purple for custom data source
        }
        
        // Use original colors for unchanged or scaled data
        if (dataPoint.data_source === 'Replica') return '#3b82f6';
        if (dataPoint.data_source === 'EMFAC Backcasted') return '#f59e0b';
        if (dataPoint.data_source === 'Population Backcasted') return '#f59e0b';
        if (dataPoint.data_source === 'Combined Backcasted') return '#f59e0b'; // Same color as EMFAC/Population for 2005-2014
        if (dataPoint.data_source === 'EMFAC Fleet Backcasted') return '#10b981';
        return '#10b981'; // AADT Backcasted (default)
      };

      const backgroundColors = sortedData.map(getDataSourceColor);
      const borderColors = backgroundColors.map(color => color);

      // Create datasets with proper legend labels
      const hasModifiedData = backgroundColors.some(color => color === '#dc2626');
      const hasCustomData = backgroundColors.some(color => color === '#9333ea');
      const hasEmfacData = backgroundColors.some(color => color === '#f59e0b');
      const hasAadtData = backgroundColors.some(color => color === '#10b981');
      const hasReplicaData = backgroundColors.some(color => color === '#3b82f6');

      chartInstance.current.data = {
        labels: years,
        datasets: [{
          label: 'Vehicle Miles Traveled (Annual)',
          data: vmtValues,
          backgroundColor: backgroundColors,
          borderColor: borderColors,
          borderWidth: 1
        }]
      };

      // Update legend to show data source information
      if (chartInstance.current.options.plugins) {
        chartInstance.current.options.plugins.legend = {
        display: true,
        position: 'bottom',
        labels: {
          generateLabels: function() {
            const labels: any[] = [];
            if (hasReplicaData) {
              // Determine which Replica years are present
              const replicaYears = sortedData
                .filter((d: any) => d.data_source === 'Replica')
                .map((d: any) => d.year)
                .sort((a: number, b: number) => a - b);
              const replicaYearRange = replicaYears.length > 0 
                ? `(${replicaYears.join(', ')})`
                : '(2023-2025)';
              labels.push({
                text: `Replica ${replicaYearRange}`,
                fillStyle: '#3b82f6',
                strokeStyle: '#3b82f6',
                lineWidth: 1,
                pointStyle: 'rect' as const
              });
            }
            if (hasEmfacData) {
              labels.push({
                text: 'EMFAC Backcasted (2005-2014)',
                fillStyle: '#f59e0b',
                strokeStyle: '#f59e0b',
                lineWidth: 1,
                pointStyle: 'rect' as const
              });
            }
            if (hasAadtData) {
              labels.push({
                text: 'AADT Backcasted (2015-2022)',
                fillStyle: '#10b981',
                strokeStyle: '#10b981',
                lineWidth: 1,
                pointStyle: 'rect' as const
              });
            }
            if (hasModifiedData) {
              labels.push({
                text: 'User Modified Values',
                fillStyle: '#dc2626',
                strokeStyle: '#dc2626',
                lineWidth: 1,
                pointStyle: 'rect' as const
              });
            }
            if (hasCustomData) {
              labels.push({
                text: 'Custom Data Source',
                fillStyle: '#9333ea',
                strokeStyle: '#9333ea',
                lineWidth: 1,
                pointStyle: 'rect' as const
              });
            }
            return labels;
          }
        }
        };
      }
      
      chartInstance.current.update('none'); // Update without animation for better performance
    }
  }, [originalData, modifiedYears]);

  const handleDataTableChange = useCallback((updatedData: any[]) => {
    // Track which years have been manually modified and find the changed value
    const newModifiedYears = new Set(modifiedYears);
    let modifiedYear: number | null = null;
    let modifiedVMT: number | null = null;
    
    // Find which year was just modified by comparing to current table data
    updatedData.forEach(newItem => {
      const currentItem = tableData.find(d => d.year === newItem.year);
      if (currentItem && Math.abs(newItem.vmt - currentItem.vmt) > 1) {
        newModifiedYears.add(newItem.year);
        modifiedYear = newItem.year;
        modifiedVMT = newItem.vmt;
      }
    });
    
    // Scale all unmodified years based on the modified value
    let finalData = updatedData;
    if (modifiedYear !== null && modifiedVMT !== null) {
      finalData = scaleDataToModifiedValue(updatedData, modifiedYear, modifiedVMT);
    }
    
    setModifiedYears(newModifiedYears);
    setTableData(finalData);
    setChartData(finalData);
    
    // Update chart with new data
    updateChart(finalData);
    
    // Notify parent component of data change
    if (onDataCalculated) {
      onDataCalculated(finalData);
    }
  }, [tableData, modifiedYears, scaleDataToModifiedValue, onDataCalculated, updateChart]);

  const handleRestoreDefaults = useCallback(() => {
    if (originalData.length > 0) {
      const restoredData = [...originalData];
      setTableData(restoredData);
      setChartData(restoredData);
      setModifiedYears(new Set()); // Reset modified years tracking
      
      // Update the chart with original data
      if (chartInstance.current) {
        const sortedData = restoredData.sort((a, b) => a.year - b.year);
        const years = sortedData.map(d => d.year);
        const vmtValues = sortedData.map(d => Math.round(d.vmt));
        
        // Create background colors based on original data sources (no custom data)
        const backgroundColors = sortedData.map(d => {
          if (d.data_source === 'Replica') return '#3b82f6';
          if (d.data_source === 'EMFAC Backcasted') return '#f59e0b';
          if (d.data_source === 'Combined Backcasted') return '#10b981';
          if (d.data_source === 'EMFAC Fleet Backcasted') return '#10b981';
          return '#10b981';
        });

        const borderColors = backgroundColors.map(color => color);

        chartInstance.current.data = {
          labels: years,
          datasets: [{
            label: 'Vehicle Miles Traveled (Annual)',
            data: vmtValues,
            backgroundColor: backgroundColors,
            borderColor: borderColors,
            borderWidth: 1
          }]
        };

        // Reset legend to original state (no custom data)
        const hasReplicaData = backgroundColors.some(color => color === '#3b82f6');
        const hasEmfacData = backgroundColors.some(color => color === '#f59e0b');
        const hasAadtData = backgroundColors.some(color => color === '#10b981');
        const hasCombinedData = sortedData.some((d: any) => d.data_source === 'Combined Backcasted');
        const hasEmfacFleetData = sortedData.some((d: any) => d.data_source === 'EMFAC Fleet Backcasted');

        if (chartInstance.current.options.plugins) {
          chartInstance.current.options.plugins.legend = {
          display: !embedOnly,
          position: 'bottom',
          labels: {
            generateLabels: function() {
              const labels: any[] = [];
              if (hasReplicaData) {
                // Determine which Replica years are present
                const replicaYears = sortedData
                  .filter((d: any) => d.data_source === 'Replica')
                  .map((d: any) => d.year)
                  .sort((a: number, b: number) => a - b);
                const replicaYearRange = replicaYears.length > 0 
                  ? `(${replicaYears.join(', ')})`
                  : '(2023-2025)';
                labels.push({
                  text: `Replica ${replicaYearRange}`,
                  fillStyle: '#3b82f6',
                  strokeStyle: '#3b82f6',
                  lineWidth: 1,
                  pointStyle: 'rect' as const
                });
              }
              if (hasEmfacData) {
                let labelText: string;
                if (sortedData.some((d: any) => d.data_source === 'Combined Backcasted')) {
                  labelText = 'Combined Backcasted (2005-2014)';
                } else if (sortedData.some((d: any) => d.data_source === 'Population Backcasted')) {
                  labelText = 'Population Backcasted (2005-2014)';
                } else {
                  labelText = 'EMFAC Backcasted (2005-2014)';
                }
                labels.push({
                  text: labelText,
                  fillStyle: '#f59e0b',
                  strokeStyle: '#f59e0b',
                  lineWidth: 1,
                  pointStyle: 'rect' as const
                });
              }
              if (hasCombinedData) {
                labels.push({
                  text: 'Combined Backcasted (2015-2022)',
                  fillStyle: '#10b981',
                  strokeStyle: '#10b981',
                  lineWidth: 1,
                  pointStyle: 'rect' as const
                });
              } else if (hasEmfacFleetData) {
                labels.push({
                  text: 'EMFAC Fleet Backcasted (2015-2022)',
                  fillStyle: '#10b981',
                  strokeStyle: '#10b981',
                  lineWidth: 1,
                  pointStyle: 'rect' as const
                });
              } else if (hasAadtData) {
                labels.push({
                  text: 'AADT Backcasted (2015-2022)',
                  fillStyle: '#10b981',
                  strokeStyle: '#10b981',
                  lineWidth: 1,
                  pointStyle: 'rect' as const
                });
              }
              return labels;
            }
          }
          };
        }
        
        chartInstance.current.update('none');
      }
      
      // Notify parent component of data change
      if (onDataCalculated) {
        onDataCalculated(restoredData);
      }
    }
  }, [originalData, embedOnly, onDataCalculated]);

  // Memoize data table content so it can be used in useEffect
  const dataTableContent = useMemo(() => {
    if (tableData.length === 0) return null;
    
    return (
      <div>
        {modifiedYears.size > 0 && (
          <div className="modified-years-info" style={{ marginBottom: '16px' }}>
            <small>
              <strong>Modified years:</strong> {Array.from(modifiedYears).sort().join(', ')} 
              (other years scaled proportionally)
            </small>
          </div>
        )}
        
        <DataTable
          data={tableData}
          highlightedRows={modifiedYears}
          highlightKey="year"
          columns={[
            { key: 'year', label: 'Year', type: 'year', editable: true, 
              validate: (value) => {
                const year = parseInt(value);
                if (isNaN(year) || year < 1990 || year > 2050) {
                  return 'Year must be between 1990 and 2050';
                }
                return null;
              }
            },
            { key: 'vmt', label: 'VMT (Annual)', type: 'number', editable: true,
              format: (value) => typeof value === 'number' ? Math.round(value).toLocaleString() : value,
              validate: (value) => {
                const vmt = parseFloat(value);
                if (isNaN(vmt) || vmt < 0) {
                  return 'VMT must be a positive number';
                }
                return null;
              }
            },
            { key: 'data_source', label: 'Data Source', type: 'text', editable: true },
            { key: 'location', label: 'Location', type: 'text', editable: false }
          ]}
          onDataChange={handleDataTableChange}
          onRestoreDefaults={handleRestoreDefaults}
          title="VMT Data"
          maxHeight="500px"
          pageSize={15}
        />
      </div>
    );
  }, [tableData, modifiedYears, handleDataTableChange, handleRestoreDefaults]);

  // Download handlers
  const handleDownloadPNG = useCallback(() => {
    const viewLabel = chartView === 'total' ? 'Total' : chartView === 'percapita' ? 'Per_Capita' : 'Mode_Share';
    const filename = formatFilename(location, `VMT_Chart_${viewLabel}`);
    downloadChartAsPNG(chartRef, filename);
  }, [chartView, location]);

  const handleDownloadCSV = useCallback(() => {
    const viewLabel = chartView === 'total' ? 'Total' : chartView === 'percapita' ? 'Per_Capita' : 'Mode_Share';
    const filename = formatFilename(location, `VMT_Data_${viewLabel}`);
    downloadDataAsCSV(chartData, filename);
  }, [chartView, location, chartData]);

  // Expose data table content and toggle function to parent ChartLayout
  useEffect(() => {
    if (onDataTableReady && dataTableContent) {
      onDataTableReady(dataTableContent, toggleDataTable);
    }
  }, [dataTableContent, onDataTableReady, toggleDataTable]);

  // Expose download handlers to parent ChartLayout
  useEffect(() => {
    if (onDownloadPNGReady) {
      onDownloadPNGReady(handleDownloadPNG);
    }
  }, [onDownloadPNGReady, handleDownloadPNG]);

  useEffect(() => {
    if (onDownloadCSVReady) {
      onDownloadCSVReady(handleDownloadCSV);
    }
  }, [onDownloadCSVReady, handleDownloadCSV]);

  // Cleanup effect to destroy charts on unmount
  useEffect(() => {
    return () => {
      if (chartInstance.current) {
        chartInstance.current.destroy();
      }
    };
  }, []);

  if (loading) {
    return (
      <div style={{ height: '400px', width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div>Loading VMT data...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ height: '400px', width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#dc2626' }}>
        <div>{error}</div>
      </div>
    );
  }

  const chartContent = (
    <div style={{ height: embedOnly ? '380px' : '400px', width: '100%', position: 'relative' }}>
      <canvas ref={chartRef} style={{ height: '100%', width: '100%' }} />
    </div>
  );

  // Return just the chart content - ChartLayout wrapper is handled by Transportation.tsx
  return (
    <div className="vmt-chart-container">
      {chartContent}
    </div>
  );
};
